import React from "react";
import {
  buildTermCalendarCells,
  formatShortDate,
  parseLocalDate,
  toISODate,
} from "helpers/calendar/calendarDates";
import { getDayInfo } from "helpers/calendar/calendarTerms";

const WEEKDAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"];

const DayCell = ({
  date,
  config,
  autoDraftDates,
  closedDates,
  closureLabels,
  today,
  compact = false,
  onSelect,
  selectedDate,
}) => {
  if (!date) {
    return (
      <span
        className="calendar-day calendar-day--empty"
        aria-hidden="true"
      />
    );
  }

  const info = getDayInfo(
    date,
    config,
    autoDraftDates,
    closedDates,
    closureLabels,
    today
  );
  const iso = toISODate(date);
  const isSelected = selectedDate && toISODate(selectedDate) === iso;

  const classNames = [
    "calendar-day",
    compact && "calendar-day--compact",
    !info.term && "calendar-day--outside-term",
    info.isClosed && "calendar-day--closed",
    info.isAutoDraft && "calendar-day--auto-draft",
    info.isToday && "calendar-day--today",
    isSelected && "calendar-day--selected",
  ]
    .filter(Boolean)
    .join(" ");

  const labelParts = [formatShortDate(date)];
  if (info.isClosed) labelParts.push(info.closureLabel || "Closed");
  if (info.isAutoDraft) labelParts.push("Fee auto-draft");

  return (
    <button
      type="button"
      className={classNames}
      onClick={() => onSelect?.(date, info)}
      aria-label={labelParts.join(", ")}
      aria-pressed={isSelected || undefined}
    >
      <span className="calendar-day__number">{date.getDate()}</span>
    </button>
  );
};

export const TermMiniCalendar = ({
  term,
  config,
  autoDraftDates,
  closedDates,
  closureLabels,
  today,
  isCurrent,
  onSelectDay,
  selectedDate,
}) => {
  const start = parseLocalDate(term.startDate);
  const end = parseLocalDate(term.endDate);
  const cells = buildTermCalendarCells(start, end);

  return (
    <article
      className={`calendar-term ${isCurrent ? "calendar-term--current" : ""}`}
      id={`term-${term.id}`}
      aria-labelledby={`term-${term.id}-title`}
    >
      <header className="calendar-term__header">
        <h3 className="calendar-term__title" id={`term-${term.id}-title`}>
          {term.name}
        </h3>
        <p className="calendar-term__range">
          {formatShortDate(start)} – {formatShortDate(end)}
        </p>
      </header>

      <div className="calendar-term__weekdays" aria-hidden="true">
        {WEEKDAY_LABELS.map((label, index) => (
          <span key={`${term.id}-wd-${index}`} className="calendar-term__weekday">
            {label}
          </span>
        ))}
      </div>

      <div className="calendar-term__grid">
        {cells.map((date, index) => (
          <DayCell
            key={date ? toISODate(date) : `blank-${index}`}
            date={date}
            config={config}
            autoDraftDates={autoDraftDates}
            closedDates={closedDates}
            closureLabels={closureLabels}
            today={today}
            compact
            onSelect={onSelectDay}
            selectedDate={selectedDate}
          />
        ))}
      </div>
    </article>
  );
};

export default DayCell;
